import { computed, unref } from 'vue';

/**
 * useStepper — Lógica presentacional del indicador de pasos Stepper.
 *
 * @param {import('vue').Ref<string[]>|string[]} steps       — Etiquetas de cada paso.
 * @param {import('vue').Ref<number>|number}     currentStep — Paso activo 1-indexed.
 *
 * @returns {{
 *   progressFraction: import('vue').ComputedRef<number>,
 *   isCompleted:      (stepIndex: number) => boolean,
 *   isActive:         (stepIndex: number) => boolean,
 *   isActiveOrDone:   (stepIndex: number) => boolean,
 * }
 * }
 */
export function useStepper(steps, currentStep) {
    /**
     * Fracción de progreso entre 0 y 1.
     *
     * 0   → paso 1: la línea de progreso tiene ancho 0, completamente oculta
     *               bajo el primer círculo.
     * 1   → último paso o posterior: llega hasta el centro del último círculo.
     *
     * El template convierte este valor en CSS usando --progress-fraction y
     * --total-steps para calcular el ancho real sin necesitar medir el DOM.
     */
    const progressFraction = computed(() => {
        const total = unref(steps).length;
        const current = unref(currentStep);

        if (total <= 1) return 0;
        if (current >= total) return 1;

        return (current - 1) / (total - 1);
    });

    /** ¿Está este paso completado (ya pasado)? */
    const isCompleted = stepIndex => stepIndex + 1 < unref(currentStep);

    /** ¿Es el paso activo actual? */
    const isActive = stepIndex => stepIndex + 1 === unref(currentStep);

    /** ¿Está este paso activo o ya completado? */
    const isActiveOrDone = stepIndex => stepIndex + 1 <= unref(currentStep);

    return { progressFraction, isCompleted, isActive, isActiveOrDone };
}
